import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useMapStoreContext } from '@/microfrontends/map/shared/context/mapStoreContext';
import { PinService } from '@/microfrontends/map/shared/services/PinService';
import { PIN_EXTENDED_TYPES } from '@/microfrontends/map/shared/utils/mapModules/PinsLayer/types';

import { useGetClusterByActivePinId } from './useGetClusterByActivePinId';

import styles from './useUpdatePinMarkup.css';

/**
 * Хук отвечает за отрисовку разметки НАД пином, когда пин активен (нажат).
 * Только для пинов без балунов, у них своя логика формирования отображения пина и балуна над ним.
 * Работает просто - смотрим, изменился ли активный пин, и если да - формируем разметку.
 */
export function useUpdatePinMarkup(): void {
  const { ymaps, map } = useMapStoreContext();
  const activeCluster = useGetClusterByActivePinId();

  const placemarkObject = useRef<YMaps.IGeoObject | null>(null);

  const pinService = useMemo<PinService | undefined>(() => {
    if (activeCluster) {
      return new PinService({ cluster: activeCluster });
    }

    return;
  }, [activeCluster]);

  const prepareIconLayout = useCallback(() => {
    if (pinService) {
      const extendedTypes = pinService.provideExtendedTypes();
      const withoutCount = !extendedTypes.has(PIN_EXTENDED_TYPES.withCount);
      const count = pinService.provideCount();

      const className = clsx(
        styles['container'],
        styles['container--blue'],
        withoutCount && styles['container--small'],
      );

      return ymaps.templateLayoutFactory.createClass(`<div class="${className}">${count}</div>`);
    }

    return;
  }, [ymaps.templateLayoutFactory, pinService]);

  useEffect(() => {
    // Удалили старую разметку для более на активного пина
    if (placemarkObject.current) {
      map.geoObjects.remove(placemarkObject.current);
      placemarkObject.current = null;
    }

    // Если есть новый активный пин - добавили разметку для него
    if (activeCluster && pinService) {
      const pinExtendedTypes = pinService.provideExtendedTypes();
      const withBalloon = pinExtendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);

      // У пина с балуном своя разметка, его пропускаем
      if (withBalloon) {
        return;
      }

      placemarkObject.current = new ymaps.GeoObject(activeCluster, { iconLayout: prepareIconLayout() });

      map.geoObjects.add(placemarkObject.current);
    }
  }, [map.geoObjects, ymaps.GeoObject, activeCluster, pinService, prepareIconLayout]);
}
